/**
 * What the two context surfaces do when the backend refuses an entry — one rule, both screens.
 *
 * `ChooseContextComponent` (right after sign-in) and `ContextSwitcherComponent` (the chrome control on every
 * shell) ask the same endpoint the same question and can receive the same refusals, so the answer belongs in
 * one place. It did not: the chooser mapped the code to German and navigated, the switcher rendered the
 * server's English `description` and stayed put — the shift-plan defect (#386/#392) all over again, on the
 * screen where an account first meets the product.
 *
 * Two rules this module exists to keep, both easy to break by writing the handling at the call site:
 *
 * 1. **Branch on `code`, never render `description`.** That field is developer-facing English from the server's
 *    `ErrorCatalog` — see the frontend style guide, *"Surfacing an API error"*. An unmapped code degrades to a
 *    fixed German sentence, never to the raw field.
 * 2. **One refusal is a navigation, not a message.** `E_AUTH_ACCOUNT_SECURITY_RESTRICTED` names a remedy that
 *    is a whole screen; a sentence beside the cards would leave somebody re-pressing a card that cannot open
 *    until they have enrolled.
 */

import {AppRoutes} from '../../enums/routes';
import {SecondFactorErrorCodes} from '../two-factor/second-factor-errors';

/**
 * The query parameter with which a context surface opens {@link AppRoutes.TwoFactorRequired}, naming why.
 *
 * **This exists because "restricted" and "refused at entry" are two different states**, and only the first is a
 * claim on the session.
 *
 * A password login that owes a factor is minted `acct_sec=restricted` and `AuthGuard` reads that claim. A
 * *context entry* refused by `ContextSessionService.AccountSecurityRefusal` mints nothing at all: the session
 * stays an ordinary contextless one, deliberately, because it may still enter a company that requires nothing —
 * restricting it would lock the account out of every OTHER tenant it holds a grant in, which is a bigger
 * lockout than the one being prevented (`account-two-factor.md`, *"It refuses; it does not mint a restricted
 * session"*).
 *
 * So the session that most needs the remediation screen is the one whose claim says it is fine, and the guard's
 * "nothing left to remediate" bounce threw it straight back off. Carrying the reason in the URL is what lets
 * the guard tell the two apart across the full document load the entry surfaces perform.
 *
 * **It widens nothing.** It only suppresses a bounce *off* the enrolment screen; a genuinely restricted session
 * is still pinned there by the claim, and forging the parameter buys an attacker a screen that grants nothing.
 */
export const CONTEXT_ENTRY_REFUSAL_PARAM = 'refused';

/** The one value {@link CONTEXT_ENTRY_REFUSAL_PARAM} takes. */
export const CONTEXT_ENTRY_REFUSAL_VALUE = 'context-entry';

/**
 * Where a self-remediable context refusal sends the person.
 *
 * A full URL string rather than router commands, because both call sites navigate with
 * `window.open(url, '_self')`: entering a context can change which shell is correct, so the app is reloaded
 * rather than routed. That is also why the reason has to survive in the URL — nothing in memory does.
 */
export const TWO_FACTOR_REMEDIATION_URL =
  `/${AppRoutes.TwoFactorRequired}?${CONTEXT_ENTRY_REFUSAL_PARAM}=${CONTEXT_ENTRY_REFUSAL_VALUE}`;

/**
 * Whether `url` was opened by a refused context entry.
 *
 * Parses the raw URL rather than taking an `ActivatedRouteSnapshot`, so `AuthGuard` — which already decides
 * everything else from the single `state.url` string — does not grow a second source for the same fact.
 *
 * @param url a router URL, with or without a query (`/two-factor-required?refused=context-entry`).
 */
export function declaresContextEntryRefusal(url: string | null | undefined): boolean {
  if (!url) return false;
  const query = url.split('?')[1];
  if (!query) return false;

  // `URLSearchParams` rather than a substring test: `?refused=context-entry-elsewhere` and a value carried in
  // some *other* parameter must both read as false, and a hand-rolled `includes` says true to both.
  return new URLSearchParams(query.split('#')[0]).get(CONTEXT_ENTRY_REFUSAL_PARAM)
    === CONTEXT_ENTRY_REFUSAL_VALUE;
}

/**
 * Whether this refusal is the one the person can fix themselves, by enrolling an authenticator.
 *
 * Its counterpart `E_AUTH_ACCOUNT_SECURITY_LEVEL_UNREACHABLE` deliberately is *not*: a `Federated` requirement
 * means signing in as a different kind of account, which no button on any screen can produce. The two are
 * separate codes precisely so this can be a branch rather than a guess — see `AccountSecurityPolicy`.
 */
export function isSelfRemediableContextRefusal(code: string | null | undefined): boolean {
  return code === SecondFactorErrorCodes.Restricted;
}

/**
 * Why a context could not be entered, in German.
 *
 * One explicit sentence per code actually handled, each naming the next action, plus a **fixed** German
 * fallback — an unmapped code degrades to that, never to the server's `description`.
 *
 * `E_AUTH_ACCOUNT_SECURITY_RESTRICTED` is absent on purpose: it is answered by navigating to
 * {@link TWO_FACTOR_REMEDIATION_URL}, so a sentence for it would only ever be rendered by a caller that had
 * forgotten to navigate.
 */
export function contextEntryMessageFor(code: string | null | undefined): string {
  switch (code) {
    case SecondFactorErrorCodes.LevelUnreachable:
      return 'Diese Firma verlangt eine Anmeldung über einen Anbieter (z. B. Microsoft). Mit einem Konto aus ' +
        'E-Mail und Passwort ist das nicht möglich — wenden Sie sich an die Verwaltung dieser Firma.';
    case 'E_CONTEXT_NOT_GRANTED':
      return 'Für diesen Bereich liegt keine Berechtigung (mehr) vor. Bitten Sie um eine neue Einladung.';
    case 'E_EMPLOYMENT_ENDED':
      return 'Dieses Beschäftigungsverhältnis ist beendet, daher steht dieser Bereich nicht mehr offen.';
    default:
      return CONTEXT_ENTRY_FALLBACK_MESSAGE;
  }
}

/**
 * The sentence for an unrecognised entry failure.
 *
 * Deliberately says what failed and that it can be retried, rather than apologising in the abstract: this is
 * also what a network failure and a proxy's HTML error page become, neither of which carries a code at all.
 */
export const CONTEXT_ENTRY_FALLBACK_MESSAGE = 'Dieser Bereich konnte nicht geöffnet werden.';
